/**
 * ResellerDetailPage — full detail view for a single reseller or freelancer profile.
 *
 * This page handles both reseller (/resellers/:id) and freelancer (/freelancers/:id) views.
 * The `mode` prop distinguishes which entity is rendered.
 *
 * Features:
 * - Profile info card (user, company, commission type, territory, exclusivity, contract status)
 * - Edit form with all mutable fields
 * - Contract status badge + contract URL link
 * - Activate/deactivate toggle (admin only)
 * - Loading / error states
 * - Breadcrumb back to /resellers
 *
 * Hooks: useReseller / useFreelancer + useUpdateReseller / useUpdateFreelancer
 */

import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  useReseller,
  useFreelancer,
  useUpdateReseller,
  useUpdateFreelancer,
  type UpdateResellerDTO,
  type UpdateFreelancerDTO,
} from '@/hooks/useResellers'
import {
  useSendResellerContract,
  useRefreshResellerContractStatus,
  useSendFreelancerContract,
  useRefreshFreelancerContractStatus,
} from '@/hooks/useContracts'

// ── Helpers ───────────────────────────────────────────────────────────────────

const CONTRACT_STATUS_COLORS: Record<string, string> = {
  draft: 'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-blue-100 text-blue-800',
  signed: 'bg-green-100 text-green-800',
}

const CONTRACT_STATUS_LABELS: Record<string, string> = {
  draft: 'Borrador',
  in_progress: 'En progreso',
  signed: 'Firmado',
}

const COMMISSION_TYPE_LABELS: Record<string, string> = {
  percentage: 'Porcentaje',
  fixed: 'Fijo',
}

function ContractStatusBadge({ status }: { status: string }) {
  const color = CONTRACT_STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-600'
  const label = CONTRACT_STATUS_LABELS[status] ?? status
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${color}`}
    >
      {label}
    </span>
  )
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('es-CO', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

// ── Edit Reseller Form ────────────────────────────────────────────────────────

interface EditResellerFormProps {
  initial: {
    companyName: string | null
    whiteLabel: boolean
    commissionType: string
    territorialExclusivity: boolean
    territoryId: string | null
    contractStatus: string
    contractUrl: string | null
    zohoRequestId: string | null
    active: boolean
  }
  onSubmit: (data: UpdateResellerDTO) => Promise<void>
  onCancel: () => void
  isSubmitting: boolean
  error: string | null
}

function EditResellerForm({
  initial,
  onSubmit,
  onCancel,
  isSubmitting,
  error,
}: EditResellerFormProps) {
  const [companyName, setCompanyName] = useState(initial.companyName ?? '')
  const [commissionType, setCommissionType] = useState(initial.commissionType)
  const [whiteLabel, setWhiteLabel] = useState(initial.whiteLabel)
  const [territorialExclusivity, setTerritorialExclusivity] = useState(
    initial.territorialExclusivity,
  )
  const [territoryId, setTerritoryId] = useState(initial.territoryId ?? '')
  const [contractStatus, setContractStatus] = useState(initial.contractStatus)
  const [contractUrl, setContractUrl] = useState(initial.contractUrl ?? '')
  const [zohoRequestId, setZohoRequestId] = useState(initial.zohoRequestId ?? '')
  const [active, setActive] = useState(initial.active)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onSubmit({
      companyName: companyName.trim() || null,
      commissionType: commissionType as 'percentage' | 'fixed',
      whiteLabel,
      territorialExclusivity,
      territoryId: territoryId.trim() || null,
      contractStatus: contractStatus as 'draft' | 'in_progress' | 'signed',
      contractUrl: contractUrl.trim() || null,
      zohoRequestId: zohoRequestId.trim() || null,
      active,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Empresa</label>
          <input
            type="text"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="Nombre de empresa"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Tipo comision</label>
          <select
            value={commissionType}
            onChange={(e) => setCommissionType(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="percentage">Porcentaje</option>
            <option value="fixed">Fijo</option>
          </select>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Estado contrato</label>
          <select
            value={contractStatus}
            onChange={(e) => setContractStatus(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="draft">Borrador</option>
            <option value="in_progress">En progreso</option>
            <option value="signed">Firmado</option>
          </select>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">URL contrato</label>
          <input
            type="url"
            value={contractUrl}
            onChange={(e) => setContractUrl(e.target.value)}
            placeholder="https://..."
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">UUID territorio</label>
          <input
            type="text"
            value={territoryId}
            onChange={(e) => setTerritoryId(e.target.value)}
            placeholder="UUID del territorio"
            className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Zoho Request ID</label>
          <input
            type="text"
            value={zohoRequestId}
            onChange={(e) => setZohoRequestId(e.target.value)}
            placeholder="ID de solicitud Zoho Sign"
            className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <input
            id="edit-whiteLabel"
            type="checkbox"
            checked={whiteLabel}
            onChange={(e) => setWhiteLabel(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor="edit-whiteLabel" className="text-sm text-gray-700">
            White label
          </label>
        </div>
        <div className="flex items-center gap-2">
          <input
            id="edit-exclusivity"
            type="checkbox"
            checked={territorialExclusivity}
            onChange={(e) => setTerritorialExclusivity(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor="edit-exclusivity" className="text-sm text-gray-700">
            Exclusividad territorial
          </label>
        </div>
        <div className="flex items-center gap-2">
          <input
            id="edit-active"
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor="edit-active" className="text-sm text-gray-700">
            Activo
          </label>
        </div>
      </div>

      <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting ? 'Guardando...' : 'Guardar cambios'}
        </button>
      </div>
    </form>
  )
}

// ── Edit Freelancer Form ──────────────────────────────────────────────────────

interface EditFreelancerFormProps {
  initial: {
    specialty: string | null
    contractStatus: string
    contractUrl: string | null
    zohoRequestId: string | null
    active: boolean
  }
  onSubmit: (data: UpdateFreelancerDTO) => Promise<void>
  onCancel: () => void
  isSubmitting: boolean
  error: string | null
}

function EditFreelancerForm({
  initial,
  onSubmit,
  onCancel,
  isSubmitting,
  error,
}: EditFreelancerFormProps) {
  const [specialty, setSpecialty] = useState(initial.specialty ?? '')
  const [contractStatus, setContractStatus] = useState(initial.contractStatus)
  const [contractUrl, setContractUrl] = useState(initial.contractUrl ?? '')
  const [zohoRequestId, setZohoRequestId] = useState(initial.zohoRequestId ?? '')
  const [active, setActive] = useState(initial.active)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onSubmit({
      specialty: specialty.trim() || null,
      contractStatus: contractStatus as 'draft' | 'in_progress' | 'signed',
      contractUrl: contractUrl.trim() || null,
      zohoRequestId: zohoRequestId.trim() || null,
      active,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Especialidad</label>
          <input
            type="text"
            value={specialty}
            onChange={(e) => setSpecialty(e.target.value)}
            placeholder="E-commerce, Restaurantes..."
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Estado contrato</label>
          <select
            value={contractStatus}
            onChange={(e) => setContractStatus(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="draft">Borrador</option>
            <option value="in_progress">En progreso</option>
            <option value="signed">Firmado</option>
          </select>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">URL contrato</label>
          <input
            type="url"
            value={contractUrl}
            onChange={(e) => setContractUrl(e.target.value)}
            placeholder="https://..."
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Zoho Request ID</label>
          <input
            type="text"
            value={zohoRequestId}
            onChange={(e) => setZohoRequestId(e.target.value)}
            placeholder="ID de solicitud Zoho Sign"
            className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input
          id="f-edit-active"
          type="checkbox"
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <label htmlFor="f-edit-active" className="text-sm text-gray-700">
          Activo
        </label>
      </div>

      <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting ? 'Guardando...' : 'Guardar cambios'}
        </button>
      </div>
    </form>
  )
}

// ── Reseller Detail Page ──────────────────────────────────────────────────────

export function ResellerDetailPage() {
  const { id } = useParams<{ id: string }>()
  const resellerId = id ?? ''

  const { data: reseller, isLoading, isError } = useReseller(resellerId)
  const updateMutation = useUpdateReseller()
  const sendContractMutation = useSendResellerContract()
  const refreshStatusMutation = useRefreshResellerContractStatus()

  const [showEditModal, setShowEditModal] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)
  const [contractError, setContractError] = useState<string | null>(null)

  const handleSendContract = async () => {
    setContractError(null)
    try {
      await sendContractMutation.mutateAsync(resellerId)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al enviar contrato'
      setContractError(message)
    }
  }

  const handleRefreshStatus = async () => {
    setContractError(null)
    try {
      await refreshStatusMutation.mutateAsync(resellerId)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al actualizar estado'
      setContractError(message)
    }
  }

  const handleUpdate = async (data: UpdateResellerDTO) => {
    setEditError(null)
    try {
      await updateMutation.mutateAsync({ id: resellerId, data })
      setShowEditModal(false)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al actualizar'
      setEditError(message)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-gray-500">Cargando revendedor...</p>
      </div>
    )
  }

  if (isError || !reseller) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <p className="text-red-500">Error al cargar el revendedor</p>
        <Link
          to="/resellers"
          className="mt-4 text-sm text-blue-600 hover:text-blue-800"
        >
          &larr; Volver a canal de socios
        </Link>
      </div>
    )
  }

  return (
    <div>
      {/* Breadcrumb */}
      <div className="mb-6">
        <Link
          to="/resellers"
          className="mb-2 inline-block text-sm text-blue-600 hover:text-blue-800"
        >
          &larr; Volver a canal de socios
        </Link>

        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">
            {reseller.user_name ?? `Revendedor ${resellerId.slice(0, 8)}`}
          </h1>
          {reseller.company_name && (
            <span className="text-gray-400">—</span>
          )}
          {reseller.company_name && (
            <span className="text-lg text-gray-600">{reseller.company_name}</span>
          )}
          <ContractStatusBadge status={reseller.contract_status} />
          <button
            onClick={() => {
              setEditError(null)
              setShowEditModal(true)
            }}
            className="ml-auto rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Editar
          </button>
        </div>
      </div>

      {/* Update error banner */}
      {updateMutation.isError && (
        <div className="mb-4 rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-700">{updateMutation.error?.message}</p>
        </div>
      )}

      {/* Profile info card */}
      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-base font-semibold text-gray-900">Perfil revendedor</h2>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">Usuario</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {reseller.user_name ?? '-'}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">Email</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {reseller.user_email ? (
                <a
                  href={`mailto:${reseller.user_email}`}
                  className="text-blue-600 hover:text-blue-800"
                >
                  {reseller.user_email}
                </a>
              ) : (
                '-'
              )}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">Empresa</dt>
            <dd className="mt-1 text-sm text-gray-900">{reseller.company_name ?? '-'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">
              Tipo comision
            </dt>
            <dd className="mt-1 text-sm text-gray-900">
              {COMMISSION_TYPE_LABELS[reseller.commission_type] ?? reseller.commission_type}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">
              White label
            </dt>
            <dd className="mt-1 text-sm text-gray-900">
              {reseller.white_label ? 'Si' : 'No'}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">
              Exclusividad territorial
            </dt>
            <dd className="mt-1 text-sm text-gray-900">
              {reseller.territorial_exclusivity ? 'Si' : 'No'}
            </dd>
          </div>
          {reseller.territory_name && (
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">
                Territorio
              </dt>
              <dd className="mt-1 text-sm text-gray-900">{reseller.territory_name}</dd>
            </div>
          )}
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">
              Estado contrato
            </dt>
            <dd className="mt-1">
              <ContractStatusBadge status={reseller.contract_status} />
            </dd>
          </div>
          {reseller.contract_url && (
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">
                URL contrato
              </dt>
              <dd className="mt-1 text-sm">
                <a
                  href={reseller.contract_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Ver contrato
                </a>
              </dd>
            </div>
          )}
          {reseller.zoho_request_id && (
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">
                Zoho Request ID
              </dt>
              <dd className="mt-1 font-mono text-xs text-gray-600">{reseller.zoho_request_id}</dd>
            </div>
          )}
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">Estado</dt>
            <dd className="mt-1">
              {reseller.active ? (
                <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                  Activo
                </span>
              ) : (
                <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                  Inactivo
                </span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">Creado</dt>
            <dd className="mt-1 text-sm text-gray-900">{formatDate(reseller.created_at)}</dd>
          </div>
        </dl>
      </div>

      {/* Contract actions card */}
      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-base font-semibold text-gray-900">Contrato Zoho Sign</h2>

        {contractError && (
          <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{contractError}</div>
        )}

        <div className="flex flex-wrap items-center gap-4">
          {/* Status display */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Estado:</span>
            <ContractStatusBadge status={reseller.contract_status} />
          </div>

          {/* Signed: show contract link */}
          {reseller.contract_status === 'signed' && (
            <div className="flex items-center gap-2 text-sm text-green-700">
              <span>Contrato firmado</span>
              {reseller.contract_url && (
                <a
                  href={reseller.contract_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Ver documento
                </a>
              )}
            </div>
          )}

          {/* Draft: show send button */}
          {reseller.contract_status === 'draft' && (
            <button
              onClick={handleSendContract}
              disabled={sendContractMutation.isPending}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {sendContractMutation.isPending ? 'Enviando...' : 'Enviar Contrato'}
            </button>
          )}

          {/* In progress: show refresh button */}
          {reseller.contract_status === 'in_progress' && (
            <button
              onClick={handleRefreshStatus}
              disabled={refreshStatusMutation.isPending}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {refreshStatusMutation.isPending ? 'Actualizando...' : 'Actualizar Estado'}
            </button>
          )}
        </div>
      </div>

      {/* Edit modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-2xl rounded-lg bg-white shadow-xl">
            <div className="border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">Editar revendedor</h2>
            </div>
            <div className="max-h-[80vh] overflow-y-auto p-6">
              <EditResellerForm
                initial={{
                  companyName: reseller.company_name,
                  whiteLabel: reseller.white_label,
                  commissionType: reseller.commission_type,
                  territorialExclusivity: reseller.territorial_exclusivity,
                  territoryId: reseller.territory_id,
                  contractStatus: reseller.contract_status,
                  contractUrl: reseller.contract_url,
                  zohoRequestId: reseller.zoho_request_id,
                  active: reseller.active,
                }}
                onSubmit={handleUpdate}
                onCancel={() => setShowEditModal(false)}
                isSubmitting={updateMutation.isPending}
                error={editError}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Freelancer Detail Page ────────────────────────────────────────────────────

export function FreelancerDetailPage() {
  const { id } = useParams<{ id: string }>()
  const freelancerId = id ?? ''

  const { data: freelancer, isLoading, isError } = useFreelancer(freelancerId)
  const updateMutation = useUpdateFreelancer()
  const sendContractMutation = useSendFreelancerContract()
  const refreshStatusMutation = useRefreshFreelancerContractStatus()

  const [showEditModal, setShowEditModal] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)
  const [contractError, setContractError] = useState<string | null>(null)

  const handleSendContract = async () => {
    setContractError(null)
    try {
      await sendContractMutation.mutateAsync(freelancerId)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al enviar contrato'
      setContractError(message)
    }
  }

  const handleRefreshStatus = async () => {
    setContractError(null)
    try {
      await refreshStatusMutation.mutateAsync(freelancerId)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al actualizar estado'
      setContractError(message)
    }
  }

  const handleUpdate = async (data: UpdateFreelancerDTO) => {
    setEditError(null)
    try {
      await updateMutation.mutateAsync({ id: freelancerId, data })
      setShowEditModal(false)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al actualizar'
      setEditError(message)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-gray-500">Cargando freelancer...</p>
      </div>
    )
  }

  if (isError || !freelancer) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <p className="text-red-500">Error al cargar el freelancer</p>
        <Link
          to="/resellers"
          className="mt-4 text-sm text-blue-600 hover:text-blue-800"
        >
          &larr; Volver a canal de socios
        </Link>
      </div>
    )
  }

  return (
    <div>
      {/* Breadcrumb */}
      <div className="mb-6">
        <Link
          to="/resellers"
          className="mb-2 inline-block text-sm text-blue-600 hover:text-blue-800"
        >
          &larr; Volver a canal de socios
        </Link>

        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">
            {freelancer.user_name ?? `Freelancer ${freelancerId.slice(0, 8)}`}
          </h1>
          {freelancer.specialty && (
            <>
              <span className="text-gray-400">—</span>
              <span className="text-lg text-gray-600">{freelancer.specialty}</span>
            </>
          )}
          <ContractStatusBadge status={freelancer.contract_status} />
          <button
            onClick={() => {
              setEditError(null)
              setShowEditModal(true)
            }}
            className="ml-auto rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Editar
          </button>
        </div>
      </div>

      {/* Update error banner */}
      {updateMutation.isError && (
        <div className="mb-4 rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-700">{updateMutation.error?.message}</p>
        </div>
      )}

      {/* Profile info card */}
      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-base font-semibold text-gray-900">Perfil freelancer</h2>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">Usuario</dt>
            <dd className="mt-1 text-sm text-gray-900">{freelancer.user_name ?? '-'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">Email</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {freelancer.user_email ? (
                <a
                  href={`mailto:${freelancer.user_email}`}
                  className="text-blue-600 hover:text-blue-800"
                >
                  {freelancer.user_email}
                </a>
              ) : (
                '-'
              )}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">
              Especialidad
            </dt>
            <dd className="mt-1 text-sm text-gray-900">{freelancer.specialty ?? '-'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">
              Estado contrato
            </dt>
            <dd className="mt-1">
              <ContractStatusBadge status={freelancer.contract_status} />
            </dd>
          </div>
          {freelancer.contract_url && (
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">
                URL contrato
              </dt>
              <dd className="mt-1 text-sm">
                <a
                  href={freelancer.contract_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Ver contrato
                </a>
              </dd>
            </div>
          )}
          {freelancer.zoho_request_id && (
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">
                Zoho Request ID
              </dt>
              <dd className="mt-1 font-mono text-xs text-gray-600">
                {freelancer.zoho_request_id}
              </dd>
            </div>
          )}
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">Estado</dt>
            <dd className="mt-1">
              {freelancer.active ? (
                <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                  Activo
                </span>
              ) : (
                <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                  Inactivo
                </span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">Creado</dt>
            <dd className="mt-1 text-sm text-gray-900">{formatDate(freelancer.created_at)}</dd>
          </div>
        </dl>
      </div>

      {/* Contract actions card */}
      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-base font-semibold text-gray-900">Contrato Zoho Sign</h2>

        {contractError && (
          <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{contractError}</div>
        )}

        <div className="flex flex-wrap items-center gap-4">
          {/* Status display */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Estado:</span>
            <ContractStatusBadge status={freelancer.contract_status} />
          </div>

          {/* Signed: show contract link */}
          {freelancer.contract_status === 'signed' && (
            <div className="flex items-center gap-2 text-sm text-green-700">
              <span>Contrato firmado</span>
              {freelancer.contract_url && (
                <a
                  href={freelancer.contract_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Ver documento
                </a>
              )}
            </div>
          )}

          {/* Draft: show send button */}
          {freelancer.contract_status === 'draft' && (
            <button
              onClick={handleSendContract}
              disabled={sendContractMutation.isPending}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {sendContractMutation.isPending ? 'Enviando...' : 'Enviar Contrato'}
            </button>
          )}

          {/* In progress: show refresh button */}
          {freelancer.contract_status === 'in_progress' && (
            <button
              onClick={handleRefreshStatus}
              disabled={refreshStatusMutation.isPending}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {refreshStatusMutation.isPending ? 'Actualizando...' : 'Actualizar Estado'}
            </button>
          )}
        </div>
      </div>

      {/* Edit modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
            <div className="border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">Editar freelancer</h2>
            </div>
            <div className="max-h-[80vh] overflow-y-auto p-6">
              <EditFreelancerForm
                initial={{
                  specialty: freelancer.specialty,
                  contractStatus: freelancer.contract_status,
                  contractUrl: freelancer.contract_url,
                  zohoRequestId: freelancer.zoho_request_id,
                  active: freelancer.active,
                }}
                onSubmit={handleUpdate}
                onCancel={() => setShowEditModal(false)}
                isSubmitting={updateMutation.isPending}
                error={editError}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
